import React, { createContext, useContext, useState } from "react";

import { Button } from "@/src/components/ui/button";
import {
  Dialog as DialogRoot,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/src/components/ui/dialog";
import { cn } from "@/src/lib/utils";

import type { ComponentProps, JSX, ReactNode } from "react";

const DialogContainerContext = createContext<HTMLElement | null>(null);

/**
 * The dialog's content element. Nested popovers (comboboxes, date pickers)
 * should portal into it via `popoverContainer` so they render above the overlay
 * and clicks inside them don't dismiss the dialog. Only valid inside `Dialog`.
 */
export function useDialogContainer(): HTMLElement | null {
  return useContext(DialogContainerContext);
}

interface DialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  cancelLabel: string;
  confirmLabel: string;
  onConfirm: () => void;
  isConfirmDisabled?: boolean;
  isCancelHidden?: boolean;
  confirmVariant?: ComponentProps<typeof Button>["variant"];
  contentClassName?: string;
}

/**
 * Form/confirmation dialog built on the shadcn `dialog` primitives, mirroring
 * `AlertDialog`. Renders a header (title/description), a body slot, and a
 * cancel/confirm footer. Closing via escape/overlay and the cancel button both
 * flow through `onOpenChange`. Descendants can reach the content element with
 * `useDialogContainer()` to portal nested popovers inside the dialog.
 */
function Dialog({
  isOpen,
  onOpenChange,
  title,
  description,
  children,
  cancelLabel,
  confirmLabel,
  onConfirm,
  isConfirmDisabled,
  isCancelHidden,
  confirmVariant,
  contentClassName,
}: DialogProps): JSX.Element {
  const [container, setContainer] = useState<HTMLElement | null>(null);

  return (
    <DialogRoot onOpenChange={onOpenChange} open={isOpen}>
      <DialogContent className={cn("bg-card", contentClassName)}>
        <div ref={setContainer}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description ? (
              <DialogDescription>{description}</DialogDescription>
            ) : null}
          </DialogHeader>

          <div className="flex flex-col gap-4 py-4">
            <DialogContainerContext.Provider value={container}>
              {children}
            </DialogContainerContext.Provider>
          </div>

          <DialogFooter>
            {isCancelHidden ? null : (
              <Button
                onClick={() => onOpenChange(false)}
                type="button"
                variant="outline"
              >
                {cancelLabel}
              </Button>
            )}
            <Button
              disabled={isConfirmDisabled}
              onClick={onConfirm}
              type="button"
              variant={confirmVariant}
            >
              {confirmLabel}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </DialogRoot>
  );
}

export default Dialog;
